import { Outlet, NavLink, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  TestTube2,
  Play,
  ClipboardCheck,
  BarChart3,
  ArrowLeft,
  Menu,
  X,
  Workflow,
} from 'lucide-react';
import { clsx } from 'clsx';
import { useState } from 'react';
import { UserMenu } from '../../components/UserMenu';
import { api } from '../../lib/api';

interface NavBadgeCounts {
  pendingRuns: number;
  pendingEvaluations: number;
}

const validationNavigation = [
  { name: 'Test Cases', href: '/validation/test-cases', icon: TestTube2 },
  { name: 'Test Runs', href: '/validation/test-runs', icon: Play, badgeKey: 'pendingRuns' as const },
  { name: 'Evaluations', href: '/validation/evaluations', icon: ClipboardCheck, badgeKey: 'pendingEvaluations' as const },
  { name: 'Analytics', href: '/validation/analytics', icon: BarChart3 },
  { name: 'Workflow', href: '/validation/production-workflow', icon: Workflow },
];

export function ValidationLayout() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Fetch badge counts for navigation
  const { data: badgeCounts } = useQuery<{ data: NavBadgeCounts }>({
    queryKey: ['validation', 'nav-badges'],
    queryFn: () => api.get('/api/validation/analytics/nav-badges'),
    refetchInterval: 30000, // Refresh every 30 seconds
    staleTime: 10000,
  });

  const badges = badgeCounts?.data || { pendingRuns: 0, pendingEvaluations: 0 };

  const renderNavBadge = (count: number | undefined) => {
    if (!count || count === 0) return null;
    return (
      <span className="ml-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 text-xs font-medium text-white">
        {count > 99 ? '99+' : count}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-gray-200 bg-white shadow-sm">
        <div className="flex h-12 items-center justify-between px-4 lg:px-6">
          <div className="flex items-center gap-4">
            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="sm:hidden p-1.5 rounded-lg text-gray-500 hover:bg-gray-100"
              aria-label="Toggle navigation menu"
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>

            {/* Back to main app */}
            <Link
              to="/"
              className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">BetterMatch</span>
            </Link>

            <div className="h-5 w-px bg-gray-300 hidden sm:block" />

            {/* Validation brand */}
            <NavLink to="/validation" className="flex items-center gap-2">
              <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-amber-600">
                <TestTube2 className="h-4 w-4 text-white" />
              </div>
              <span className="text-base font-semibold font-display text-gray-900 hidden sm:inline">
                Model Validation
              </span>
            </NavLink>

            {/* Desktop Navigation */}
            <nav className="ml-4 hidden sm:flex gap-0.5">
              {validationNavigation.map((item) => (
                <NavLink
                  key={item.name}
                  to={item.href}
                  className={({ isActive }) =>
                    clsx(
                      'flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm transition-all duration-150',
                      isActive
                        ? 'bg-amber-100 text-amber-800 font-semibold ring-1 ring-amber-200'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 font-medium'
                    )
                  }
                >
                  <item.icon className="h-4 w-4" aria-hidden="true" />
                  <span className="hidden md:inline">{item.name}</span>
                  {item.badgeKey && renderNavBadge(badges[item.badgeKey])}
                </NavLink>
              ))}
            </nav>
          </div>

          <UserMenu />
        </div>

        {/* Mobile Navigation Drawer */}
        {mobileMenuOpen && (
          <div className="sm:hidden border-t border-gray-200 bg-white">
            <nav className="p-2 space-y-1">
              {validationNavigation.map((item) => (
                <NavLink
                  key={item.name}
                  to={item.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    clsx(
                      'flex items-center gap-3 rounded-lg px-4 py-3 text-sm transition-all duration-150',
                      isActive
                        ? 'bg-amber-100 text-amber-800 font-semibold'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    )
                  }
                >
                  <item.icon className="h-5 w-5" aria-hidden="true" />
                  <span>{item.name}</span>
                  {item.badgeKey && renderNavBadge(badges[item.badgeKey])}
                </NavLink>
              ))}
            </nav>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="px-4 py-4 lg:px-6 lg:py-5">
        <Outlet />
      </main>
    </div>
  );
}
